/**
 * Cursor composto da inbox: `last_message_at DESC NULLS LAST, id DESC`.
 *
 * Não reutiliza `buildConversationKeysetOrFilter` (conversations-export.ts):
 * lá a ordem é `created_at ASC` + `id.gt`. Aqui é DESC e precisa tratar
 * `last_message_at IS NULL` (que ordena por último).
 */

export interface InboxKeysetCursor {
  lastMessageAt: string | null;
  id: string;
}

/**
 * Filtro PostgREST `.or(...)` que devolve estritamente as linhas
 * posteriores ao cursor na ordenação da inbox.
 */
export function buildInboxKeysetOrFilter(cursor: InboxKeysetCursor): string {
  if (!cursor.lastMessageAt) {
    // Cursor já está no bloco de NULLs (fim da lista): só ids menores, todos NULL.
    return `and(last_message_at.is.null,id.lt.${cursor.id})`;
  }
  return [
    `last_message_at.lt.${cursor.lastMessageAt}`,
    `last_message_at.is.null`,
    `and(last_message_at.eq.${cursor.lastMessageAt},id.lt.${cursor.id})`,
  ].join(",");
}

/** Ordena igual ao servidor — usado no merge local para evitar duplicata/gap. */
export function compareInboxKeysetDesc(
  a: InboxKeysetCursor,
  b: InboxKeysetCursor,
): number {
  if (a.lastMessageAt === b.lastMessageAt) return b.id.localeCompare(a.id);
  if (!a.lastMessageAt) return 1;
  if (!b.lastMessageAt) return -1;
  return b.lastMessageAt.localeCompare(a.lastMessageAt);
}

/** Merge sem duplicar (dedupe por id, mantendo a ordem do keyset). */
export function mergeInboxKeysetPages<T extends { conversation_id: string }>(
  current: T[],
  next: T[],
): T[] {
  const seen = new Set(current.map((row) => row.conversation_id));
  const appended = next.filter((row) => {
    if (seen.has(row.conversation_id)) return false;
    seen.add(row.conversation_id);
    return true;
  });
  return appended.length === 0 ? current : [...current, ...appended];
}
